import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Globe, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { approveListing, revokeListing } from "@admin/services/adminApi";
import { formatLawyerDisplayName } from "@admin/lib/lawyerDisplayName";

const MIN_REASON = 10;

export function isActiveAccount(l) {
  return !l.account_status || l.account_status === "active";
}

function describeError(e, fallback) {
  const st = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (st === 404) return "Lawyer not found. The list has been refreshed.";
  if (st === 409) return typeof detail === "string" ? detail : "State changed — refreshing.";
  if (st === 403) return typeof detail === "string" ? detail : "You don't have permission for this action.";
  if (st === 422) return "Please provide a reason of at least 10 characters.";
  return typeof detail === "string" ? detail : fallback;
}

export function ListingActions({ lawyer, onChanged, onDialogOpenChange }) {
  const [mode, setMode] = useState(null); // "approve" | "revoke"
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // Lets a containing menu stay open while the confirm dialog is up.
  useEffect(() => { onDialogOpenChange?.(!!mode); }, [mode, onDialogOpenChange]);
  const canApprove = lawyer.verified && !lawyer.listing_approved && isActiveAccount(lawyer);
  const canRevoke = !!lawyer.listing_approved;
  if (!canApprove && !canRevoke) return null;

  const close = () => { setMode(null); setNote(""); setReason(""); };
  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "approve") {
        await approveListing(lawyer.id, note.trim() || undefined);
        toast.success(`${formatLawyerDisplayName(lawyer.name)} is now listed on the marketplace`);
      } else {
        await revokeListing(lawyer.id, reason.trim());
        toast.success(`${formatLawyerDisplayName(lawyer.name)} unlisted — verification unchanged`);
      }
      close();
      onChanged();
    } catch (e) {
      const st = e?.response?.status;
      if (st === 403 && e?.response?.data?.detail?.code === "TWO_FACTOR_REQUIRED") return; // handled by api interceptor
      toast.error(describeError(e, "Action failed"));
      if (st === 404 || st === 409) { close(); onChanged(); }
    } finally { setBusy(false); }
  };
  const reasonInvalid = mode === "revoke" && reason.trim().length < MIN_REASON;

  return (
    <>
      {canApprove && (
        <button onClick={() => setMode("approve")} data-testid={`listing-approve-${lawyer.id}`}
          className="px-2 py-1 text-xs font-bold rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-left">
          <Globe size={10} className="inline mr-0.5" /> Approve Listing
        </button>
      )}
      {canRevoke && (
        <button onClick={() => setMode("revoke")} data-testid={`listing-revoke-${lawyer.id}`}
          className="px-2 py-1 text-xs font-bold rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50 text-left">
          <EyeOff size={10} className="inline mr-0.5" /> Unlist
        </button>
      )}
      <AlertDialog open={!!mode} onOpenChange={(o) => { if (!o && !busy) close(); }}>
        <AlertDialogContent data-testid={`listing-dialog-${lawyer.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="listing-dialog-title">
              {mode === "approve" ? "Approve this lawyer for marketplace listing?" : "Unlist this lawyer from the marketplace?"}
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="listing-dialog-body">
              {mode === "approve" ? (
                <>
                  <strong>{formatLawyerDisplayName(lawyer.name)}</strong> is verified. Once approved, this lawyer's profile can be visible to
                  clients on the Advoset marketplace. Availability (Online/Offline) remains controlled separately by the
                  lawyer and is not changed by this approval.
                </>
              ) : (
                <>
                  This will remove <strong>{formatLawyerDisplayName(lawyer.name)}</strong> from public marketplace visibility and make them
                  unavailable for active marketplace use. Their verified status will not be changed.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {mode === "approve" ? (
            <Textarea rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note (max 500 characters)" data-testid="listing-note" className="rounded-xl text-sm" />
          ) : (
            <div>
              <Textarea rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Reason for unlisting (required, min 10 characters)" data-testid="listing-reason"
                className="rounded-xl text-sm" />
              {reason.length > 0 && reasonInvalid && (
                <p className="mt-1 text-xs text-rose-600" data-testid="listing-reason-error">
                  Reason must be at least {MIN_REASON} characters ({reason.trim().length}/{MIN_REASON}).
                </p>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="listing-dialog-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button onClick={(e) => { e.preventDefault(); submit(); }} disabled={busy || reasonInvalid}
                data-testid="listing-dialog-confirm"
                className={mode === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}>
                {busy && <Loader2 size={12} className="animate-spin mr-1" />}
                {mode === "approve" ? "Approve Listing" : "Unlist"}
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
